export type CanvasThemeMode = "light" | "dark";

export interface CanvasHeroImageFields {
  /** @deprecated Use `hero_image_light_url` / `hero_image_dark_url`. */
  hero_image_url?: string;
  hero_image_light_url?: string;
  hero_image_dark_url?: string;
}

function resolveLightHeroImageUrl(
  canvas: CanvasHeroImageFields,
): string | undefined {
  return canvas.hero_image_light_url || canvas.hero_image_url;
}

function resolveDarkHeroImageUrl(
  canvas: CanvasHeroImageFields,
): string | undefined {
  return canvas.hero_image_dark_url;
}

/** Presigned hero/thumbnail URL for the active UI theme. */
export function getCanvasHeroImageUrl(
  canvas: CanvasHeroImageFields | undefined,
  mode: CanvasThemeMode,
): string | undefined {
  if (!canvas) {
    return undefined;
  }

  const themedUrl =
    mode === "dark"
      ? resolveDarkHeroImageUrl(canvas)
      : resolveLightHeroImageUrl(canvas);

  return themedUrl || resolveLightHeroImageUrl(canvas);
}

function didThemedHeroImageUrlChange(
  prevUrl: string | undefined,
  nextUrl: string | undefined,
): boolean {
  return Boolean(nextUrl && nextUrl !== prevUrl);
}

/**
 * True when a canvas.updated payload carries new light and dark hero presigns.
 * Both themed URLs must change before treating capture as complete — with
 * separate light/dark uploads, a single-theme update must not clear
 * `metadata.hero_generating` while the other capture is still in flight.
 */
export function didCanvasHeroImagesChange(
  prev: CanvasHeroImageFields,
  next: CanvasHeroImageFields,
): boolean {
  const lightChanged = didThemedHeroImageUrlChange(
    resolveLightHeroImageUrl(prev),
    resolveLightHeroImageUrl(next),
  );
  const darkChanged = didThemedHeroImageUrlChange(
    resolveDarkHeroImageUrl(prev),
    resolveDarkHeroImageUrl(next),
  );

  return lightChanged && darkChanged;
}
